import { twMerge } from 'tailwind-merge';
import {
  useCurrentUser,
  useDispatch,
  useKeypoint,
  useKeypointType,
  useProjectFromKeypointId,
} from '@witmetrics/data-store';
import { formatUserName } from '@witmetrics/utils/strings';
import { isEmpty } from '@witmetrics/utils/misc';
import { isCustomContent } from '@witmetrics/utils/keypoints';
import { getProjectDisplayName } from '@witmetrics/unison-core';
import { showSuccessToaster } from '@/utils/toasters';
import { useAppState } from '@/providers/AppStateProvider';
import { useToggle } from '@/hooks/useToggle';
import KeypointContent from '@/components/KeypointContent';
import NotesField from '@/components/inputs/NotesField';
import DeleteKeypointDialog from '@/dialogs/DeleteKeypointDialog';
import Card from '@/components/Card';
import CaseTypeIcon from '@/components/CaseTypeIcon';
import KeypointHeader from './KeypointHeader';
import type { TimelineVariant } from '../types';
import { classes, styles, updateKeypoint } from './utils';

export type KeypointBlockProps = {
  showProject?: boolean;
  editable?: boolean;
  variant?: TimelineVariant;
  keypointId: number;
};

export default function KeypointBlock({
  showProject,
  editable,
  variant = 'default',
  keypointId,
}: KeypointBlockProps) {
  const dispatch = useDispatch();
  const { onApiError, onSelectProject } = useAppState();
  const currentUser = useCurrentUser();
  const keypoint = useKeypoint(keypointId);
  const keypointType = useKeypointType(keypoint?.keypointTypeId);
  const project = useProjectFromKeypointId(keypointId);
  const [isEditing, toggleEditing] = useToggle();
  const [isSaving, toggleSaving] = useToggle();
  const [isDeleting, toggleDeleting] = useToggle();

  const handleSave = async (message: string) => {
    try {
      toggleSaving(true);
      const updatedKeypoint = await updateKeypoint(keypointId, {
        keypointTypeId: keypoint!.keypointTypeId, // TODO
        message,
        startsAt: new Date(keypoint!.startsAt), // TODO
      });
      dispatch.addKeypoints([updatedKeypoint]);
      toggleSaving(false);
      toggleEditing(false);
      showSuccessToaster('Keypoint updated');
    } catch (err) {
      toggleSaving(false);
      onApiError(err, 'Error saving keypoint');
    }
  };

  if (!keypoint || !keypointType) return null;

  return (
    <>
      {isDeleting && (
        <DeleteKeypointDialog
          keypointId={keypointId}
          onClose={() => toggleDeleting(false)}
        />
      )}
      <Card
        sx={
          variant === 'aurora'
            ? { ...styles.card, ...styles.auroraDark }
            : styles.card
        }
        className={twMerge(
          classes.card,
          variant === 'aurora' && classes.auroraCard
        )}>
        <div className={classes.contentWrapper}>
          <KeypointHeader
            deletable={editable}
            editable={
              editable &&
              isCustomContent(keypoint.content) &&
              keypoint.createdBy?.id === currentUser?.id
            }
            className={classes.header}
            title={formatUserName(keypoint.createdBy)}
            timestamp={keypoint.startsAt}
            onEdit={() => toggleEditing(true)}
            onDelete={() => toggleDeleting(true)}
          />
          {showProject && project && (
            <div className={classes.linkWrapper}>
              <CaseTypeIcon
                className={classes.caseType}
                projectTypeId={project.unisonProjectTypeId}
                iconClassName={classes.caseTypeIcon}
              />
              <button
                className={classes.linkName}
                onClick={() => onSelectProject(project.id)}>
                {getProjectDisplayName(project)}
                {!isEmpty(project.externalRef) && ` (${project.externalRef})`}
              </button>
            </div>
          )}
          {isEditing ? (
            <NotesField
              isSaving={isSaving}
              placeholder="Add a note..."
              initialMessage={
                'message' in keypoint.content ? keypoint.content.message : '--'
              }
              users={[]} /* TODO: Make sure tagging is disabled */
              onSave={handleSave}
              onCancel={() => toggleEditing()}
            />
          ) : (
            <KeypointContent
              className={classes.content}
              keypointId={keypointId}
            />
          )}
        </div>
      </Card>
    </>
  );
}
